import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  catPrecheck,
  icmRequestStatus,
  initiateIcmRequest,
  catResults,
  icmJobHistory,
  downloadAllIcmArtifacts,
  initiateRmsEltRequest,
  rmsEltRequestStatus,
  rmsEltJobHistory,
  downloadAllRmsEltArtifacts,
  rerunRmsEltFinancials,
  resumeRmsEltPipeline,
  type EltPipelineType,
  initiateRms22Request,
  rms22RequestStatus,
  rms22JobHistory,
  downloadAllRms22Artifacts,
  initiateIcmYltRequest,
  icmYltRequestStatus,
  icmYltJobHistory,
  downloadAllIcmYltArtifacts,
  initiateSpatialKatRequest,
  spatialKatRequestStatus,
  spatialKatJobHistory,
  downloadAllSpatialKatArtifacts,
  enqueueRater,
} from "../../../api/routes";
import { useCatModelPolling } from "./useCatModelPolling";
import { NearmapTab } from "./NearmapTab";
import { useTriggerNearmap } from "./data";
import { SafeReportResponse } from "../Conversion";
import styles from "./CatCard.module.css";
import {
  WorkStatus,
  CatResultsResponse,
  CatJobHistoryItem,
  CatJobHistoryResponse,
  CatPrecheckResponse,
  VendorPrecheck,
} from "../../../client/types.gen";
import { useToast } from "../../../context-providers/Toast";
import { downloadBlob } from "../../../util";
import { IfFeatureEnabled, useFeatureEnabled } from "../../../IfFeatureEnabled";
import { useLocalUser, useRatePrecheck } from "../data";
import { ModelTabOption, SelectedModel, getPerilDisplayName } from "./types";
import { useIsMobile } from "../../../responsive/useMediaQuery";
import { RaterTab } from "./RaterTab";
import { useReportJobs } from "../rater/useIntegrationJobs";
import { CatModelSwitcher } from "./CatModelSwitcher";
import { CatResultsView } from "./CatResultsView";
import { TemplateButton } from "../template/TemplateButton";
import { ParsedTemplate } from "../template/data";

interface CatCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

const LARGE_CAT_JOB_THRESHOLD = 10_000;

export const CatCard: React.FC<CatCardProps> = ({ specialtyPropertyInfo }) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [icmResultsData, setIcmResultsData] =
    useState<CatResultsResponse | null>(null);
  const [rmsEltResultsData, setRmsEltResultsData] =
    useState<CatResultsResponse | null>(null);
  const [rms22ResultsData, setRms22ResultsData] =
    useState<CatResultsResponse | null>(null);
  const [icmYltResultsData, setIcmYltResultsData] =
    useState<CatResultsResponse | null>(null);
  const [spatialKatResultsData, setSpatialKatResultsData] =
    useState<CatResultsResponse | null>(null);
  const [jobHistory, setJobHistory] = useState<CatJobHistoryItem[]>([]);
  const [viewingBatchId, setViewingBatchId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [selectedModel, setSelectedModel] = useState<ModelTabOption>(
    searchParams.get("model") === "rater" ? "rater" : "rms_elt"
  );
  const [showAllLayers, setShowAllLayers] = useState(false);
  const isMobile = useIsMobile();
  const toast = useToast();
  const user = useLocalUser();
  const catModelOverrideAllowed = useFeatureEnabled(
    "cat-model-override-allowed",
    undefined,
    user
  );
  const largeCatJobsAllowed = useFeatureEnabled(
    "large-cat-jobs-allowed",
    undefined,
    user
  );
  const nearmapTabEnabled = useFeatureEnabled(
    "show-nearmap-tab",
    undefined,
    user
  );
  const rmsRerunFinancialsEnabled = useFeatureEnabled(
    "rms-rerun-financials",
    undefined,
    user
  );
  // The batch whose resume / re-run-financials is currently being kicked
  // off, so the history row can show a spinner and the rest disable.
  const [recoveryInFlightBatchId, setRecoveryInFlightBatchId] = useState<
    string | null
  >(null);
  const numLocations = specialtyPropertyInfo.precomputed_metadata.num_locations;
  const isLargeJob = numLocations > LARGE_CAT_JOB_THRESHOLD;
  const [showLargeJobBlockedDialog, setShowLargeJobBlockedDialog] =
    useState(false);
  const [showRunModelingDialog, setShowRunModelingDialog] = useState(false);
  const [runModelingRms, setRunModelingRms] = useState(true);
  const [runModelingKatRisk, setRunModelingKatRisk] = useState(true);
  const [runModelingIcm, setRunModelingIcm] = useState(true);
  const [runModelingRater, setRunModelingRater] = useState(true);
  const [raterTemplateOverride, setRaterTemplateOverride] = useState<
    ParsedTemplate | undefined
  >(undefined);
  const [isLoadingRater, setIsLoadingRater] = useState(false);

  const guardLargeJob = (action: () => void) => {
    if (isLargeJob && !largeCatJobsAllowed) {
      setShowLargeJobBlockedDialog(true);
      return;
    }
    action();
  };
  const [precheck, setPrecheck] = useState<CatPrecheckResponse | null>(null);
  const localUser = useLocalUser();
  const reportId = specialtyPropertyInfo.report_id;

  const handleJobHistoryUpdate = useCallback(
    (modelType: string, jobs: CatJobHistoryItem[]) => {
      setJobHistory((prev) => {
        const otherJobs = prev.filter((j) => j.type !== modelType);
        return [...otherJobs, ...jobs].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );
      });
    },
    []
  );

  const triggerNearmap = useTriggerNearmap(reportId);
  const handleRunNearmap = () =>
    triggerNearmap.mutate(undefined, {
      onError: () =>
        toast.show({
          severity: "error",
          summary: "Nearmap Request Failed",
          detail: "There was an error initiating your Nearmap request.",
        }),
    });

  const icmPolling = useCatModelPolling({
    modelType: "icm",
    displayName: "ICM",
    reportId,
    toast,
    initiateRequest: initiateIcmRequest,
    checkStatus: icmRequestStatus,
    fetchJobHistory: icmJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setIcmResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  const rmsEltPolling = useCatModelPolling({
    modelType: "rms_elt",
    displayName: "RMS",
    reportId,
    toast,
    initiateRequest: initiateRmsEltRequest,
    checkStatus: rmsEltRequestStatus,
    fetchJobHistory: rmsEltJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setRmsEltResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  const rms22Polling = useCatModelPolling({
    modelType: "rms_22",
    displayName: "RMS 22",
    reportId,
    toast,
    initiateRequest: initiateRms22Request,
    checkStatus: rms22RequestStatus,
    fetchJobHistory: rms22JobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setRms22ResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  const icmYltPolling = useCatModelPolling({
    modelType: "icm_ylt",
    displayName: "ICM",
    reportId,
    toast,
    initiateRequest: initiateIcmYltRequest,
    checkStatus: icmYltRequestStatus,
    fetchJobHistory: icmYltJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setIcmYltResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  const spatialKatPolling = useCatModelPolling({
    modelType: "spatialkat",
    displayName: "SpatialKat",
    reportId,
    toast,
    initiateRequest: initiateSpatialKatRequest,
    checkStatus: spatialKatRequestStatus,
    fetchJobHistory: spatialKatJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setSpatialKatResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
    showErrorDetail: true,
  });

  const { data: ratePrecheck } = useRatePrecheck(reportId);
  const { jobs: raterJobs, fetchJobs: refetchRaterJobs } = useReportJobs(
    reportId,
    { integrationTypes: ["RATER"] }
  );
  const raterBlocked =
    !ratePrecheck || ratePrecheck.warnings.some((w) => w.blocking);
  const hasRaterResults = raterJobs.some((j) => j.outcome === "success");

  const handleRunRater = async () => {
    setIsLoadingRater(true);
    try {
      await enqueueRater(reportId, raterTemplateOverride?.template_id);
      await refetchRaterJobs();
    } finally {
      setIsLoadingRater(false);
    }
  };

  // Load existing results and check for pending jobs on mount
  useEffect(() => {
    const loadExistingResults = async () => {
      try {
        const [
          catRes,
          icmHist,
          rmsEltHist,
          rms22Hist,
          icmYltHist,
          skHist,
          precheckRes,
        ] = await Promise.all([
          catResults(specialtyPropertyInfo.report_id).catch(() => null),
          icmJobHistory(specialtyPropertyInfo.report_id),
          rmsEltJobHistory(specialtyPropertyInfo.report_id),
          rms22JobHistory(specialtyPropertyInfo.report_id).catch(
            () => ({ jobs: [] }) as CatJobHistoryResponse
          ),
          icmYltJobHistory(specialtyPropertyInfo.report_id).catch(
            () => ({ jobs: [] }) as CatJobHistoryResponse
          ),
          spatialKatJobHistory(specialtyPropertyInfo.report_id),
          catPrecheck(specialtyPropertyInfo.report_id).catch(() => null),
        ]);

        setPrecheck(precheckRes);

        if (catRes?.icm?.air?.aggregate?.layers?.length) {
          setIcmResultsData(catRes);
        }

        if (catRes?.spatialkat?.aggregate?.layers?.length) {
          setSpatialKatResultsData(catRes);
        }

        if (catRes?.rms_elt?.aggregate?.layers?.length) {
          setRmsEltResultsData(catRes);
        }

        if (catRes?.rms_22?.aggregate?.layers?.length) {
          setRms22ResultsData(catRes);
        }

        if (catRes?.icm_ylt?.aggregate?.layers?.length) {
          setIcmYltResultsData(catRes);
        }

        // Combine job histories
        const allJobs = [
          ...(icmHist.jobs ?? []),
          ...(rmsEltHist.jobs ?? []),
          ...(rms22Hist.jobs ?? []),
          ...(icmYltHist.jobs ?? []),
          ...(skHist.jobs ?? []),
        ].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );

        setJobHistory(allJobs);

        // Resume polling for any active jobs
        const getActiveIds = (hist: { jobs?: CatJobHistoryItem[] | null }) =>
          (hist.jobs ?? [])
            .filter(
              (job) =>
                job.status === WorkStatus.PENDING ||
                job.status === WorkStatus.IN_PROGRESS
            )
            .map((job) => job.batch_id);

        const activeIcmIds = getActiveIds(icmHist);
        const activeRmsEltIds = getActiveIds(rmsEltHist);
        const activeRms22Ids = getActiveIds(rms22Hist);
        const activeIcmYltIds = getActiveIds(icmYltHist);
        const activeSkIds = getActiveIds(skHist);

        if (activeIcmIds.length > 0) {
          icmPolling.setPendingRequestIds(activeIcmIds);
        }
        if (activeRmsEltIds.length > 0) {
          rmsEltPolling.setPendingRequestIds(activeRmsEltIds);
        }
        if (activeRms22Ids.length > 0) {
          rms22Polling.setPendingRequestIds(activeRms22Ids);
        }
        if (activeIcmYltIds.length > 0) {
          icmYltPolling.setPendingRequestIds(activeIcmYltIds);
        }
        if (activeSkIds.length > 0) {
          spatialKatPolling.setPendingRequestIds(activeSkIds);
        }
      } catch (error) {
        console.log("No existing CAT results found");
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadExistingResults();
  }, [specialtyPropertyInfo.report_id]);

  const rmsNeedsConfirmation =
    (precheck?.rms.warnings.length ?? 0) > 0 && !precheck?.rms.blocked;

  // Per-row recovery for the pipeline tabs (RMS / RMS 22 / ICM YLT), shown in
  // the Run History tab: each row resumes (failed) or re-runs financials (done)
  // against its own batch. All three ride the generic
  // /cat/{report_id}/{resume,rerun-financial} endpoints (the backend branches
  // on type), so EltPipelineType already covers icm_ylt. Other models don't.
  const eltRecoveryModel: EltPipelineType | null =
    rmsRerunFinancialsEnabled &&
    (selectedModel === "rms_elt" ||
      selectedModel === "rms_22" ||
      selectedModel === "icm_ylt")
      ? selectedModel
      : null;
  const eltPolling =
    eltRecoveryModel === "icm_ylt"
      ? icmYltPolling
      : eltRecoveryModel === "rms_22"
        ? rms22Polling
        : rmsEltPolling;
  const eltJobHistory =
    eltRecoveryModel === "icm_ylt"
      ? icmYltJobHistory
      : eltRecoveryModel === "rms_22"
        ? rms22JobHistory
        : rmsEltJobHistory;

  const handleRerunRmsEltFinancials = async (sourceBatchId: string) => {
    if (!eltRecoveryModel) return;
    setRecoveryInFlightBatchId(sourceBatchId);
    try {
      const result = await rerunRmsEltFinancials(
        reportId,
        eltRecoveryModel,
        sourceBatchId
      );
      eltPolling.setPendingRequestIds((prev) => [...prev, result.batch_id]);
      const history = await eltJobHistory(reportId);
      if (history.jobs) {
        handleJobHistoryUpdate(eltRecoveryModel, history.jobs);
      }
      toast.show({
        severity: "info",
        summary: "Financials Re-run Started",
        detail: "Re-running the financial model from the selected batch.",
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Re-run Failed",
        detail: "Could not start the financials re-run.",
      });
    } finally {
      setRecoveryInFlightBatchId(null);
    }
  };

  const handleResumeRmsEltPipeline = async (
    batchId: string,
    mode: "resume" | "restart"
  ) => {
    if (!eltRecoveryModel) return;
    setRecoveryInFlightBatchId(batchId);
    try {
      const result = await resumeRmsEltPipeline(
        reportId,
        eltRecoveryModel,
        batchId,
        mode
      );
      eltPolling.setPendingRequestIds((prev) =>
        prev.includes(result.batch_id) ? prev : [...prev, result.batch_id]
      );
      const history = await eltJobHistory(reportId);
      if (history.jobs) {
        handleJobHistoryUpdate(eltRecoveryModel, history.jobs);
      }
      toast.show({
        severity: "info",
        summary: mode === "restart" ? "Run Restarted" : "Run Resumed",
        detail:
          mode === "restart"
            ? "Restarting modeling with fresh submissions."
            : "Resuming the selected batch from where it stalled.",
      });
    } catch {
      toast.show({
        severity: "error",
        summary: mode === "restart" ? "Restart Failed" : "Resume Failed",
        detail:
          mode === "restart"
            ? "Could not restart the run."
            : "Could not resume the run.",
      });
    } finally {
      setRecoveryInFlightBatchId(null);
    }
  };

  const eltRecovery = eltRecoveryModel
    ? {
        onResume: (batchId: string) =>
          handleResumeRmsEltPipeline(batchId, "resume"),
        // Restart (drop the stuck ICM request, submit fresh) only makes
        // sense for ICM YLT — the RMS pipelines have no ICM status URL to
        // shed, and the backend rejects restart for them.
        onRestart:
          eltRecoveryModel === "icm_ylt"
            ? (batchId: string) =>
                handleResumeRmsEltPipeline(batchId, "restart")
            : undefined,
        onRerunFinancials: handleRerunRmsEltFinancials,
        actionInFlightBatchId: recoveryInFlightBatchId,
        disabled: eltPolling.isPending,
      }
    : null;

  const rmsBlocked = !precheck || precheck.rms.blocked;
  const spatialKatBlocked = !precheck || precheck.spatialkat.blocked;
  const icmYltBlocked = !precheck || precheck.icm.blocked;
  const rmsNotRunnable =
    rmsBlocked || (!catModelOverrideAllowed && rmsNeedsConfirmation);
  const openRunModelingDialog = () => {
    setRunModelingRms(!rmsNotRunnable);
    setRunModelingKatRisk(!spatialKatBlocked);
    setRunModelingIcm(!icmYltBlocked);
    setRunModelingRater(!raterBlocked);
    setShowRunModelingDialog(true);
  };
  const handleRunModeling = async () => {
    if (runModelingRms && isLargeJob && !largeCatJobsAllowed) {
      setShowRunModelingDialog(false);
      setShowLargeJobBlockedDialog(true);
      return;
    }
    setShowRunModelingDialog(false);
    const runs: Promise<unknown>[] = [];
    if (runModelingRms && !rmsNotRunnable) {
      runs.push(rmsEltPolling.handleInitiate());
    }
    if (runModelingKatRisk && !spatialKatBlocked) {
      runs.push(spatialKatPolling.handleInitiate());
    }
    if (runModelingIcm && !icmYltBlocked) {
      runs.push(icmYltPolling.handleInitiate());
    }
    if (runModelingRater && !raterBlocked) {
      runs.push(handleRunRater());
    }
    await Promise.all(runs);
  };

  const layerInfoById = useMemo(
    () =>
      new Map(
        specialtyPropertyInfo.report_json.layer_info.map((layer) => [
          layer.layer_id!,
          layer,
        ])
      ),
    [specialtyPropertyInfo]
  );

  const hasIcmResults =
    (icmResultsData?.icm?.air?.aggregate?.layers?.length ?? 0) > 0;
  const hasRmsEltResults =
    (rmsEltResultsData?.rms_elt?.aggregate?.layers?.length ?? 0) > 0;
  const hasRms22Results =
    (rms22ResultsData?.rms_22?.aggregate?.layers?.length ?? 0) > 0;
  const hasIcmYltResults =
    (icmYltResultsData?.icm_ylt?.aggregate?.layers?.length ?? 0) > 0;
  const hasSpatialKatResults =
    (spatialKatResultsData?.spatialkat?.aggregate?.layers?.length ?? 0) > 0;
  const hasResults =
    hasIcmResults ||
    hasRmsEltResults ||
    hasRms22Results ||
    hasIcmYltResults ||
    hasSpatialKatResults;

  const handleViewJobResults = async (job: CatJobHistoryItem) => {
    try {
      const results = await catResults(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      if (job.type === "icm") {
        setIcmResultsData(results);
      } else if (job.type === "rms_elt") {
        setRmsEltResultsData(results);
      } else if (job.type === "rms_22") {
        setRms22ResultsData(results);
      } else if (job.type === "icm_ylt") {
        setIcmYltResultsData(results);
      } else if (job.type === "spatialkat") {
        setSpatialKatResultsData(results);
      }
      setViewingBatchId(job.batch_id);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load results for this job.",
      });
    }
  };

  const handleResetToDefaultResults = async () => {
    try {
      const results = await catResults(specialtyPropertyInfo.report_id);
      setIcmResultsData(results);
      setRmsEltResultsData(results);
      setRms22ResultsData(results);
      setIcmYltResultsData(results);
      setSpatialKatResultsData(results);
      setViewingBatchId(null);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load default results.",
      });
    }
  };

  const handleDownloadAll = async (job: CatJobHistoryItem) => {
    try {
      // Each cat source has its own GCS prefix; routing icm_ylt or
      // rms_elt through the rms helper sends type=rms to the server,
      // which then looks up the wrong prefix (silent empty zip for
      // icm_ylt) or names the file misleadingly (rms_elt).
      const downloadFnByType: Partial<
        Record<
          CatJobHistoryItem["type"],
          (
            reportId: string,
            batchId: string
          ) => Promise<{ data: Blob; filename?: string }>
        >
      > = {
        icm: downloadAllIcmArtifacts,
        rms_elt: downloadAllRmsEltArtifacts,
        rms_22: downloadAllRms22Artifacts,
        icm_ylt: downloadAllIcmYltArtifacts,
        spatialkat: downloadAllSpatialKatArtifacts,
      };
      const downloadFn = downloadFnByType[job.type];
      if (!downloadFn) {
        return;
      }
      const { data, filename } = await downloadFn(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      downloadBlob(filename ?? `${job.type}_all_${job.batch_id}.zip`, data);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Download Failed",
        detail: "Could not download the artifacts zip.",
      });
    }
  };

  const raterPending = raterJobs.some((j) => !j.outcome);
  const isPending =
    icmPolling.isPending ||
    rmsEltPolling.isPending ||
    rms22Polling.isPending ||
    icmYltPolling.isPending ||
    spatialKatPolling.isPending ||
    raterPending;

  const pendingMessage = (() => {
    const active: string[] = [];
    if (rmsEltPolling.isPending) active.push("RMS");
    if (rms22Polling.isPending) active.push("RMS 22");
    if (spatialKatPolling.isPending) active.push("KatRisk");
    if (icmYltPolling.isPending) active.push("ICM");
    if (icmPolling.isPending) active.push("ICM (Legacy)");
    if (raterPending) active.push("Rater");
    if (active.length === 0) return "";
    const list =
      active.length === 1
        ? active[0]
        : active.length === 2
          ? `${active[0]} and ${active[1]}`
          : `${active.slice(0, -1).join(", ")}, and ${active[active.length - 1]}`;
    return `${list} in progress...`;
  })();

  const topLocationsByPeril =
    selectedModel === "rms_elt"
      ? rmsEltResultsData?.rms_elt?.top_locations_by_peril
      : selectedModel === "rms_22"
        ? rms22ResultsData?.rms_22?.top_locations_by_peril
        : selectedModel === "icm_ylt"
          ? icmYltResultsData?.icm_ylt?.top_locations_by_peril
          : undefined;

  return (
    <Card
      title={
        <div className={styles.titleRow}>
          <span className={styles.cardSectionTitle}>Modeling</span>
          <Button
            label={
              hasRmsEltResults ||
              hasSpatialKatResults ||
              hasIcmYltResults ||
              hasRaterResults
                ? "Re-run modeling"
                : "Run modeling"
            }
            icon="pi pi-play"
            raised
            text
            onClick={openRunModelingDialog}
            loading={isPending}
            disabled={
              !precheck ||
              (rmsNotRunnable &&
                spatialKatBlocked &&
                icmYltBlocked &&
                raterBlocked)
            }
          />
          {precheck &&
            selectedModel !== "rater" &&
            selectedModel !== "nearmap" && (
              <CatPrecheckBanner
                precheck={precheck}
                selectedModel={selectedModel}
              />
            )}
          {isMobile && (hasResults || jobHistory.length > 0) && (
            <div className={styles.headerLayerToggle}>
              <Checkbox
                inputId="show-all-layers-mobile"
                checked={showAllLayers}
                onChange={(e) => setShowAllLayers(!!e.checked)}
              />
              <label htmlFor="show-all-layers-mobile">Show all layers</label>
            </div>
          )}
        </div>
      }
      className={styles.catCard}
    >
      <div className={styles.content}>
        {isPending && (
          <Message
            severity="info"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <ProgressSpinner
                  className={styles.spinnerSmall}
                  strokeWidth="4"
                />
                <span>{pendingMessage}</span>
              </div>
            }
          />
        )}

        {viewingBatchId && (
          <Message
            severity="warn"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <span>
                  Viewing results from a historical run — these are not the
                  active results.
                </span>
                <Button
                  label="Reset to default"
                  text
                  size="small"
                  onClick={handleResetToDefaultResults}
                />
              </div>
            }
          />
        )}

        <div className={styles.modelSelectionRow}>
          <CatModelSwitcher
            selectedModel={selectedModel}
            onChange={setSelectedModel}
            isMobile={isMobile}
            nearmapTabEnabled={nearmapTabEnabled}
          />
          {selectedModel === "rater" ? (
            <>
              <Button
                label={
                  raterTemplateOverride
                    ? `Generate rater (${raterTemplateOverride.template_name})`
                    : hasRaterResults
                      ? "Re-run rater"
                      : "Generate rater"
                }
                icon="pi pi-play"
                raised
                text
                severity="secondary"
                onClick={handleRunRater}
                loading={isLoadingRater}
                disabled={isLoadingRater || raterPending || raterBlocked}
              />
              <TemplateButton
                useCase="rater"
                suffix="rater"
                onOverrideTemplateVersion={setRaterTemplateOverride}
              />
            </>
          ) : selectedModel === "icm" ? (
            <IfFeatureEnabled feature="show-icm-start-button" user={localUser}>
              <Button
                label={
                  hasIcmResults ? "Re-run ICM (Legacy)" : "Run ICM (Legacy)"
                }
                icon="pi pi-play"
                raised
                text
                severity="secondary"
                onClick={icmPolling.handleInitiate}
                loading={icmPolling.isInitiateLoading}
                disabled={
                  icmPolling.isPending || !precheck || precheck.icm.blocked
                }
              />
            </IfFeatureEnabled>
          ) : selectedModel === "icm_ylt" ? (
            <Button
              label={hasIcmYltResults ? "Re-run ICM" : "Run ICM"}
              icon="pi pi-play"
              raised
              text
              severity="secondary"
              onClick={icmYltPolling.handleInitiate}
              loading={icmYltPolling.isInitiateLoading}
              disabled={
                icmYltPolling.isPending || !precheck || precheck.icm.blocked
              }
            />
          ) : selectedModel === "rms_elt" ? (
            <Button
              label={hasRmsEltResults ? "Re-run RMS" : "Run RMS"}
              icon="pi pi-play"
              onClick={() => guardLargeJob(rmsEltPolling.handleInitiate)}
              raised
              text
              severity="secondary"
              loading={rmsEltPolling.isInitiateLoading}
              disabled={
                rmsEltPolling.isPending ||
                !precheck ||
                precheck.rms.blocked ||
                (!catModelOverrideAllowed && rmsNeedsConfirmation)
              }
            />
          ) : selectedModel === "spatialkat" ? (
            <Button
              label={
                hasSpatialKatResults
                  ? "Re-run KatRisk Flood"
                  : "Run KatRisk Flood"
              }
              icon="pi pi-play"
              raised
              text
              severity="secondary"
              onClick={spatialKatPolling.handleInitiate}
              loading={spatialKatPolling.isInitiateLoading}
              disabled={
                spatialKatPolling.isPending ||
                !precheck ||
                precheck.spatialkat.blocked
              }
            />
          ) : selectedModel === "nearmap" ? (
            <Button
              label="Run Nearmap"
              icon="pi pi-play"
              raised
              text
              severity="secondary"
              onClick={handleRunNearmap}
              loading={triggerNearmap.isPending}
              disabled={triggerNearmap.isPending}
            />
          ) : (
            <Button
              label={hasRms22Results ? "Re-run RMS 22" : "Run RMS 22"}
              icon="pi pi-play"
              onClick={() => guardLargeJob(rms22Polling.handleInitiate)}
              raised
              text
              severity="secondary"
              loading={rms22Polling.isInitiateLoading}
              disabled={
                rms22Polling.isPending ||
                !precheck ||
                precheck.rms.blocked ||
                (!catModelOverrideAllowed && rmsNeedsConfirmation)
              }
            />
          )}
        </div>
        {selectedModel === "rater" ? (
          <RaterTab reportId={reportId} />
        ) : selectedModel === "nearmap" ? (
          <NearmapTab reportId={reportId} />
        ) : hasResults || jobHistory.length > 0 ? (
          <CatResultsView
            isMobile={isMobile}
            selectedModel={selectedModel}
            icmResultsData={icmResultsData}
            rmsEltResultsData={rmsEltResultsData}
            rms22ResultsData={rms22ResultsData}
            icmYltResultsData={icmYltResultsData}
            spatialKatResultsData={spatialKatResultsData}
            layerInfoById={layerInfoById}
            showAllLayers={showAllLayers}
            onShowAllLayersChange={setShowAllLayers}
            topLocationsByPeril={topLocationsByPeril}
            reportId={reportId}
            jobHistory={jobHistory}
            viewingBatchId={viewingBatchId}
            onViewJobResults={handleViewJobResults}
            onDownloadAll={handleDownloadAll}
            eltRecovery={eltRecovery}
          />
        ) : isInitialLoading ? (
          <div className={styles.loadingContainer}>
            <ProgressSpinner />
          </div>
        ) : !isPending ? (
          <Message
            severity="info"
            text="No CAT results available yet. Run an analysis to generate results."
            className={styles.messageMargin}
          />
        ) : null}
      </div>

      <Dialog
        header="Run Modeling"
        visible={showRunModelingDialog}
        onHide={() => setShowRunModelingDialog(false)}
        draggable={false}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              severity="secondary"
              text
              onClick={() => setShowRunModelingDialog(false)}
            />
            <Button
              label="Run"
              icon="pi pi-play"
              onClick={handleRunModeling}
              loading={
                rmsEltPolling.isInitiateLoading ||
                spatialKatPolling.isInitiateLoading ||
                icmYltPolling.isInitiateLoading ||
                isLoadingRater
              }
              disabled={
                !runModelingRms &&
                !runModelingKatRisk &&
                !runModelingIcm &&
                !runModelingRater
              }
            />
          </div>
        }
      >
        <div className={styles.runModelingOptions}>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-rms"
                checked={runModelingRms}
                onChange={(e) => setRunModelingRms(!!e.checked)}
                disabled={rmsNotRunnable}
              />
              <label htmlFor="run-modeling-rms">RMS</label>
            </div>
            {precheck && <PerilList vendor={precheck.rms} />}
          </div>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-katrisk"
                checked={runModelingKatRisk}
                onChange={(e) => setRunModelingKatRisk(!!e.checked)}
                disabled={spatialKatBlocked}
              />
              <label htmlFor="run-modeling-katrisk">KatRisk Flood</label>
            </div>
            {precheck && <PerilList vendor={precheck.spatialkat} />}
          </div>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-icm"
                checked={runModelingIcm}
                onChange={(e) => setRunModelingIcm(!!e.checked)}
                disabled={icmYltBlocked}
              />
              <label htmlFor="run-modeling-icm">ICM</label>
            </div>
            {precheck && <PerilList vendor={precheck.icm} />}
          </div>
          <div className={styles.runModelingOption}>
            <Checkbox
              inputId="run-modeling-rater"
              checked={runModelingRater}
              onChange={(e) => setRunModelingRater(!!e.checked)}
              disabled={raterBlocked}
            />
            <label htmlFor="run-modeling-rater">
              Fire Rater
              {raterTemplateOverride
                ? ` (${raterTemplateOverride.template_name})`
                : ""}
            </label>
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Large Submission"
        visible={showLargeJobBlockedDialog}
        onHide={() => setShowLargeJobBlockedDialog(false)}
        draggable={false}
        footer={
          <Button
            label="OK"
            onClick={() => setShowLargeJobBlockedDialog(false)}
          />
        }
      >
        <p>
          This submission has <b>{numLocations.toLocaleString()}</b> locations,
          which exceeds the {LARGE_CAT_JOB_THRESHOLD.toLocaleString()} location
          limit for running CAT jobs. Please ping John, Sean, or Andrew to run
          this for you.
        </p>
      </Dialog>
    </Card>
  );
};

const VENDOR_PRECHECK_KEY: Record<SelectedModel, "icm" | "rms" | "spatialkat"> =
  {
    icm: "icm",
    icm_ylt: "icm",
    rms_elt: "rms",
    rms_22: "rms",
    spatialkat: "spatialkat",
  };

const PerilList: React.FC<{ vendor: VendorPrecheck }> = ({ vendor }) => {
  const items = [
    ...vendor.run_perils.map((p) => ({ peril: p, run: true })),
    ...vendor.skipped_perils.map((p) => ({ peril: p, run: false })),
  ].sort((a, b) =>
    getPerilDisplayName(a.peril).localeCompare(getPerilDisplayName(b.peril))
  );
  if (items.length === 0) return null;
  return (
    <div className={styles.perilList}>
      {items.map(({ peril, run }) => (
        <div
          key={peril}
          className={`${styles.perilItem} ${
            run ? styles.perilItemRun : styles.perilItemSkip
          }`}
        >
          <i
            className={`pi ${run ? "pi-check" : "pi-times"} ${
              styles.perilItemIcon
            }`}
          />
          <span>{getPerilDisplayName(peril)}</span>
        </div>
      ))}
    </div>
  );
};

const CatPrecheckBanner: React.FC<{
  precheck: CatPrecheckResponse;
  selectedModel: SelectedModel;
}> = ({ precheck, selectedModel }) => {
  const vendor = precheck[VENDOR_PRECHECK_KEY[selectedModel]];
  const warnings = [...precheck.warnings, ...vendor.warnings];
  if (!warnings.length) return null;

  return (
    <Message
      severity={vendor.blocked ? "error" : "warn"}
      className={styles.precheckMessage}
      content={<span>{warnings.join(" ")}</span>}
    />
  );
};
